import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Archive, Code, HelpCircle, Loader2, Pencil, RotateCcw, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { StrategyComposeModal } from "@/components/Strategies/StrategyComposeModal";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { StrategySetActivePineDialog } from "@/components/Strategies/StrategySetActivePineDialog";
import { StrategyPolicyViewBody } from "../../Strategies/StrategyPolicyViewBody";
import {
  DeletePolicyVersionsDialog,
  type DeletePolicyVersionsDialogRequest,
} from "../../Strategies/DeletePolicyVersionsDialog";
import {
  getActiveComposedPolicyVersionIds,
  POLICY_VERSION_TITLE_MAX_LENGTH,
  policyVersionDisplayTitle,
} from "../../Strategies/strategyDetailUtils";
import {
  deleteReportStrategyPolicyVersion,
  getReportStrategyDetail,
  listReportStrategyPolicyVersions,
  setReportStrategyPolicyAccounts,
  updateReportStrategyPolicyVersion,
} from "../../../../services/reportsApi";
import type {
  ReportStrategyPolicyVersion,
  ReportStrategyResponse,
} from "../../../../types/reports";
import type { WorkspaceEdge, WorkspaceNode } from "../../../../types/workspace";
import {
  filterPolicyVersionsForCompose,
  strategyHasTradingViewPinescriptCreated,
} from "../../../../utils/reportStrategyPineUi";
import { presentMutationError } from "../../../../utils/showApiErrorToast";
import { StatisticsEmptyState } from "../../Statistics/components/statisticsEmptyState";
import { findUpstreamStrategyEntityId } from "../workspaceGraphUtils";
import { WorkspaceDrawerActionRow } from "../WorkspaceDrawerActionRow";
import {
  WorkspaceDrawerActionsMenu,
  type WorkspaceDrawerActionItem,
} from "../WorkspaceDrawerActionsMenu";
import {
  dispatchWorkspacePolicyPinePreviewHide,
  dispatchWorkspacePolicyPinePreviewOpen,
} from "../workspacePolicyPinePreviewEvents";
import { BrokerAccountsMultiSelect } from "@/components/Broker/BrokerAccountsMultiSelect";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount";
import type { BrokerAccountRef } from "@/utils/brokerAccountRef";
import { resolveEntityBrokerAccounts } from "@/utils/brokerAccountRef";
import { filterConnectedTradeAccountsForUi } from "@/utils/tradeAccountSource";

export type WorkspacePolicyDrawerContentProps = {
  policyVersionId: string;
  nodeId: string;
  nodes: WorkspaceNode[];
  edges: WorkspaceEdge[];
  /** Home canvas account (lineage) — not used to pre-fill executing accounts. */
  workspaceBrokerName?: string | null;
  workspaceBrokerAccountCode?: string | null;
  combinedMeta?: {
    adoptedReportId?: string | null;
    sourcePolicyVersionIds?: string[] | null;
    title?: string | null;
  };
  onClose?: () => void;
  onDeleted?: () => void;
  onAccountsSaved?: () => void | Promise<void>;
};

export function WorkspacePolicyDrawerContent({
  policyVersionId,
  nodeId,
  nodes,
  edges,
  combinedMeta,
  onClose,
  onDeleted,
  onAccountsSaved,
}: WorkspacePolicyDrawerContentProps): React.ReactElement {
  const isCombinedPolicy = Boolean(combinedMeta);
  // workspaceBrokerName / workspaceBrokerAccountCode remain on the props type for
  // callers but are intentionally unused — home account is lineage-only.
  const strategyId = findUpstreamStrategyEntityId(nodeId, nodes, edges);
  const { accounts: tradeAccounts } = useActiveBrokerAccount();
  const accountOptions = useMemo<BrokerAccountRef[]>(() => {
    return filterConnectedTradeAccountsForUi(tradeAccounts)
      .map((account) => ({
        brokerName: String(account.brokerName || "").trim().toLowerCase(),
        brokerAccountCode: String(account.accountCode || "").trim(),
      }))
      .filter((ref) => ref.brokerName && ref.brokerAccountCode);
  }, [tradeAccounts]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState<ReportStrategyPolicyVersion | null>(null);
  const [versions, setVersions] = useState<ReportStrategyPolicyVersion[]>([]);
  const [strategy, setStrategy] = useState<ReportStrategyResponse | null>(null);
  const [selectedAccounts, setSelectedAccounts] = useState<BrokerAccountRef[]>([]);
  const [accountsDirty, setAccountsDirty] = useState(false);
  const [savingAccounts, setSavingAccounts] = useState(false);

  const [mutating, setMutating] = useState(false);
  const [savingTitle, setSavingTitle] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const placeRenameCursorAtEndRef = useRef(false);
  const [deleteRequest, setDeleteRequest] = useState<DeletePolicyVersionsDialogRequest | null>(
    null
  );
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [setActiveTarget, setSetActiveTarget] = useState<ReportStrategyPolicyVersion | null>(null);
  const [setActiveBusy, setSetActiveBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [preferredComposePolicyVersionId, setPreferredComposePolicyVersionId] = useState<
    string | null
  >(null);

  const composeEligibleVersions = useMemo(
    () => filterPolicyVersionsForCompose(versions, false),
    [versions]
  );

  const activeComposedPolicyVersionIds = useMemo(
    () => getActiveComposedPolicyVersionIds(strategy?.active_pine, strategy),
    [strategy]
  );

  const reloadVersions = useCallback(async () => {
    if (!strategyId) return;
    const response = await listReportStrategyPolicyVersions(strategyId, {
      include_archived: true,
    });
    const list = response.versions ?? [];
    setVersions(list);
    const match = list.find((row) => String(row.id) === String(policyVersionId)) ?? null;
    setVersion(match);
    if (match) {
      setSelectedAccounts(resolveEntityBrokerAccounts({ accounts: match.accounts }));
      setAccountsDirty(false);
    }
    return match;
  }, [policyVersionId, strategyId]);

  useEffect(() => {
    let cancelled = false;
    if (!strategyId) {
      setLoading(false);
      setError("Could not find a parent strategy for this policy on the canvas.");
      setVersion(null);
      setVersions([]);
      setStrategy(null);
      return;
    }

    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [versionsResponse, strategyDetail] = await Promise.all([
          listReportStrategyPolicyVersions(strategyId, { include_archived: true }),
          getReportStrategyDetail(strategyId).catch(() => null),
        ]);
        if (cancelled) return;
        const list = versionsResponse.versions ?? [];
        setVersions(list);
        setStrategy(strategyDetail);
        const match =
          list.find((row) => String(row.id) === String(policyVersionId)) ?? null;
        setVersion(match);
        if (!match) {
          setError("Policy version not found on the parent strategy.");
        } else {
          setSelectedAccounts(resolveEntityBrokerAccounts({ accounts: match.accounts }));
          setAccountsDirty(false);
        }
      } catch (err) {
        if (cancelled) return;
        setVersion(null);
        setVersions([]);
        setStrategy(null);
        setError(err instanceof Error ? err.message : "Failed to load policy version.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [policyVersionId, strategyId]);

  useEffect(() => {
    return () => {
      dispatchWorkspacePolicyPinePreviewHide();
    };
  }, []);

  const handleAccountsChange = useCallback((next: BrokerAccountRef[]) => {
    setSelectedAccounts(next);
    setAccountsDirty(true);
  }, []);

  const saveAccounts = useCallback(async () => {
    if (!strategyId || !version || savingAccounts) return;
    setSavingAccounts(true);
    try {
      const updated = await setReportStrategyPolicyAccounts(strategyId, version.id, {
        accounts: selectedAccounts.map((account) => ({
          brokerName: account.brokerName.trim().toLowerCase(),
          brokerAccountCode: account.brokerAccountCode.trim(),
        })),
      });
      setVersion((prev) =>
        prev
          ? {
              ...prev,
              ...updated,
              accounts: resolveEntityBrokerAccounts({
                accounts: updated.accounts ?? selectedAccounts,
              }),
            }
          : prev
      );
      setAccountsDirty(false);
      toast.success(
        selectedAccounts.length === 0
          ? "Executing accounts cleared"
          : "Executing accounts updated"
      );
      await onAccountsSaved?.();
    } catch (err) {
      presentMutationError(err, "Could not update executing accounts");
    } finally {
      setSavingAccounts(false);
    }
  }, [onAccountsSaved, savingAccounts, selectedAccounts, strategyId, version]);

  const openPine = useCallback(() => {
    if (!strategyId || !version) return;
    const title = policyVersionDisplayTitle(version);
    dispatchWorkspacePolicyPinePreviewOpen({
      strategyId,
      policyVersionId: version.id,
      title,
      policyRowVersion: version.policy_row_version ?? null,
    });
  }, [strategyId, version]);

  const saveTitle = useCallback(
    async (title: string) => {
      if (!strategyId || !version || savingTitle) return;
      setSavingTitle(true);
      try {
        const updated = await updateReportStrategyPolicyVersion(strategyId, version.id, {
          title,
        });
        setVersion(updated);
        toast.success(title.trim() ? "Policy name saved" : "Policy name cleared");
        await reloadVersions();
        await onAccountsSaved?.();
      } catch (err) {
        presentMutationError(err, "Could not save policy name");
      } finally {
        setSavingTitle(false);
      }
    },
    [onAccountsSaved, reloadVersions, savingTitle, strategyId, version]
  );

  const storedPolicyTitle = typeof version?.title === "string" ? version.title.trim() : "";
  const renameDisplayTitle = version ? policyVersionDisplayTitle(version) : "";

  useEffect(() => {
    if (!renameOpen) return;
    setRenameDraft(renameDisplayTitle);
    placeRenameCursorAtEndRef.current = true;
  }, [renameDisplayTitle, renameOpen, version?.id]);

  const handleRenameConfirm = useCallback(async () => {
    if (!version || savingTitle) return;
    const next = renameDraft.trim().slice(0, POLICY_VERSION_TITLE_MAX_LENGTH);
    if (next === storedPolicyTitle) {
      setRenameOpen(false);
      return;
    }
    await saveTitle(next);
    setRenameOpen(false);
  }, [renameDraft, saveTitle, savingTitle, storedPolicyTitle, version]);

  const archiveOrRestore = useCallback(
    async (archive: boolean) => {
      if (!strategyId || !version || mutating) return;
      setMutating(true);
      try {
        await updateReportStrategyPolicyVersion(strategyId, version.id, {
          is_archived: archive,
        });
        toast.success(archive ? "Version archived" : "Version restored");
        await reloadVersions();
      } catch (err) {
        presentMutationError(err, archive ? "Archive failed" : "Restore failed");
      } finally {
        setMutating(false);
      }
    },
    [mutating, reloadVersions, strategyId, version]
  );

  const confirmDelete = useCallback(async () => {
    if (!strategyId || !deleteRequest || deleteBusy) return;
    const ids = Array.from(new Set(deleteRequest.policyVersionIds.filter(Boolean)));
    if (ids.length === 0) return;

    setDeleteBusy(true);
    try {
      const failures: string[] = [];
      for (const id of ids) {
        try {
          await deleteReportStrategyPolicyVersion(strategyId, id);
        } catch (err) {
          failures.push(err instanceof Error ? err.message : `Could not delete version ${id}.`);
        }
      }
      if (failures.length === 0) {
        toast.info(ids.length === 1 ? "Deleted" : `Deleted ${ids.length} policy versions`);
        setDeleteRequest(null);
        onDeleted?.();
      } else {
        presentMutationError(new Error(failures[0]), "Delete failed");
      }
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteBusy, deleteRequest, onDeleted, strategyId]);

  const confirmSetActiveComposedPine = useCallback(async () => {
    if (!setActiveTarget) return;
    setSetActiveBusy(true);
    try {
      setPreferredComposePolicyVersionId(setActiveTarget.id);
      setSetActiveTarget(null);
      setComposeOpen(true);
    } finally {
      setSetActiveBusy(false);
    }
  }, [setActiveTarget]);

  if (loading) {
    return (
      <div
        data-workspace-policy-drawer=""
        className="flex min-h-full min-w-0 flex-1 flex-col"
      >
        {onClose ? <WorkspaceDrawerActionRow onClose={onClose} className="w-full shrink-0" /> : null}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
          <StatisticsEmptyState
            variant="loading"
            fillParent
            title="Loading policy"
            description="Fetching policy details and execution rules."
          />
        </div>
      </div>
    );
  }

  if (error || !version) {
    return (
      <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
        {error ?? "Policy unavailable."}
      </p>
    );
  }

  const label = policyVersionDisplayTitle(version);
  const canCompose = Boolean(strategy) && composeEligibleVersions.some((row) => row.id === version.id);

  const drawerActionItems: WorkspaceDrawerActionItem[] = [
    {
      id: "view-pinescript",
      label: "View Pinescript",
      disabled: mutating,
      tone: "brand",
      icon: <Code className="size-4 shrink-0 text-current" aria-hidden />,
      onSelect: () => openPine(),
    },
    {
      id: "rename-policy",
      label: "Rename policy",
      disabled: mutating || deleteBusy || savingTitle,
      icon: <Pencil className="size-4 shrink-0 text-current" aria-hidden />,
      onSelect: () => setRenameOpen(true),
    },
    version.is_archived
      ? {
          id: "restore-policy",
          label: mutating ? "Restoring…" : "Restore",
          disabled: mutating,
          icon: <RotateCcw className="size-4 shrink-0 text-current" aria-hidden />,
          onSelect: () => {
            void archiveOrRestore(false);
          },
        }
      : {
          id: "archive-policy",
          label: "Archive",
          disabled: mutating,
          tone: "orange",
          icon: <Archive className="size-4 shrink-0 text-current" aria-hidden />,
          onSelect: () => {
            void archiveOrRestore(true);
          },
        },
    ...(canCompose
      ? [
          {
            id: "set-active-pine",
            label: "Set as active pine",
            disabled: mutating,
            tone: "brand" as const,
            icon: <Sparkles className="size-4 shrink-0 text-current" aria-hidden />,
            onSelect: () => setSetActiveTarget(version),
          },
        ]
      : []),
    {
      id: "delete-policy",
      label: deleteBusy ? "Deleting…" : "Delete",
      disabled: mutating || deleteBusy,
      tone: "destructive",
      separatorBefore: true,
      icon: <Trash2 className="size-4 shrink-0 text-current" aria-hidden />,
      onSelect: () => setDeleteRequest({ policyVersionIds: [version.id] }),
    },
    {
      id: "need-help",
      label: "Need Help?",
      href: "https://help.tesseratrades.com/strategies",
      tone: "brand",
      separatorBefore: true,
      icon: <HelpCircle className="size-4 shrink-0 text-current" aria-hidden />,
    },
  ];

  return (
    <div
      data-workspace-policy-drawer=""
      data-workspace-policy-kind={isCombinedPolicy ? "combined" : "single"}
      className="flex min-h-full min-w-0 flex-1 flex-col gap-4"
    >
      <WorkspaceDrawerActionRow onClose={onClose}>
        <WorkspaceDrawerActionsMenu items={drawerActionItems} />
      </WorkspaceDrawerActionRow>

      <p
        data-workspace-policy-name=""
        className="min-w-0 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"
        title={label}
      >
        {label}
      </p>

      <Field className="w-full min-w-0 gap-2">
        <FieldLabel className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
          Executing accounts
        </FieldLabel>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <BrokerAccountsMultiSelect
            options={accountOptions}
            selected={selectedAccounts}
            onChange={handleAccountsChange}
            emptyLabel="Unassigned (choose accounts…)"
            disabled={mutating || savingAccounts}
          />
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0"
            disabled={!accountsDirty || savingAccounts || mutating}
            onClick={() => void saveAccounts()}
          >
            {savingAccounts ? (
              <>
                <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              "Save accounts"
            )}
          </Button>
        </div>
        <FieldDescription className="font-[family-name:var(--font-primary)] text-xs text-muted-foreground">
          Optional assignment of which broker accounts execute this policy. Stays empty until you
          choose accounts. The home account comes from the report → simulation → policy path on the
          canvas.
        </FieldDescription>
      </Field>

      <StrategyPolicyViewBody
        title={label}
        policyTree={version.policy_tree}
        policyVersions={versions}
        policyVersion={version}
        segmentChrome={false}
        isArchived={Boolean(version.is_archived)}
      />

      <StrategyPineDetailDialog
        open={renameOpen}
        onOpenChange={(open) => {
          if (!open && savingTitle) return;
          setRenameOpen(open);
        }}
        title="Rename policy"
        showDismissButton={false}
        contentClassName="sm:max-w-md"
        footer={
          <div
            data-dashboard-layout-dialog-footer=""
            className="inline-flex max-w-full flex-wrap items-center justify-end gap-2"
          >
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={savingTitle}
              onClick={() => setRenameOpen(false)}
            >
              <span className="normal-case tracking-normal">Cancel</span>
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={
                savingTitle ||
                renameDraft.trim().slice(0, POLICY_VERSION_TITLE_MAX_LENGTH) ===
                  renameDisplayTitle.trim().slice(0, POLICY_VERSION_TITLE_MAX_LENGTH)
              }
              aria-busy={savingTitle}
              onClick={() => void handleRenameConfirm()}
            >
              <span className="normal-case tracking-normal">
                {savingTitle ? "Saving…" : "Save"}
              </span>
            </Button>
          </div>
        }
      >
        <Input
          autoFocus
          value={renameDraft}
          onChange={(e) =>
            setRenameDraft(e.target.value.slice(0, POLICY_VERSION_TITLE_MAX_LENGTH))
          }
          onFocus={(e) => {
            if (!placeRenameCursorAtEndRef.current) return;
            placeRenameCursorAtEndRef.current = false;
            const end = e.currentTarget.value.length;
            e.currentTarget.setSelectionRange(end, end);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleRenameConfirm();
            if (e.key === "Escape" && !savingTitle) setRenameOpen(false);
          }}
          maxLength={POLICY_VERSION_TITLE_MAX_LENGTH}
          aria-label="Policy name"
          placeholder={renameDisplayTitle}
          disabled={savingTitle}
          className="h-11 font-[family-name:var(--font-primary)] text-sm font-medium normal-case tracking-normal text-[color:var(--brand-tropical-surf)]"
        />
      </StrategyPineDetailDialog>

      <DeletePolicyVersionsDialog
        open={deleteRequest != null}
        onOpenChange={(open) => {
          if (!open && !deleteBusy) setDeleteRequest(null);
        }}
        request={deleteRequest}
        activeComposedPolicyVersionIds={activeComposedPolicyVersionIds}
        isDeleting={deleteBusy}
        onConfirm={() => void confirmDelete()}
      />

      {strategy ? (
        <StrategySetActivePineDialog
          open={Boolean(setActiveTarget)}
          onOpenChange={(open) => {
            if (!open && !setActiveBusy) setSetActiveTarget(null);
          }}
          policyVersionLabel={
            setActiveTarget ? policyVersionDisplayTitle(setActiveTarget) : "this policy version"
          }
          hasActiveComposedPine={strategyHasTradingViewPinescriptCreated(strategy)}
          busy={setActiveBusy}
          onConfirm={confirmSetActiveComposedPine}
        />
      ) : null}

      {strategy ? (
        <StrategyComposeModal
          shell="pine-detail"
          isOpen={composeOpen}
          onClose={() => {
            setComposeOpen(false);
            setPreferredComposePolicyVersionId(null);
          }}
          onCancel={() => {
            setComposeOpen(false);
            setPreferredComposePolicyVersionId(null);
          }}
          strategy={strategy}
          versions={composeEligibleVersions}
          currentPolicyVersionId={version.id}
          preferredPolicyVersionId={preferredComposePolicyVersionId ?? version.id}
          subtleText="#6b7280"
          textColor="inherit"
          borderColor="var(--border)"
          onStrategyRefresh={async () => {
            if (!strategyId) return;
            const next = await getReportStrategyDetail(strategyId).catch(() => null);
            if (next) setStrategy(next);
            await reloadVersions();
          }}
          onComposeSuccess={async () => {
            setComposeOpen(false);
            setPreferredComposePolicyVersionId(null);
            if (!strategyId) return;
            const next = await getReportStrategyDetail(strategyId).catch(() => null);
            if (next) setStrategy(next);
          }}
        />
      ) : null}
    </div>
  );
}
